import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import Alert from '../../Alert';
import ProductCompliancePage from './ProductCompliancePage';
import ProductWizardBasics from './ProductWizardBasics';
import ProductWizardDetails from './ProductWizardDetails';
import ProductWizardMedia from './ProductWizardMedia';
import ProductWizardShipping from './ProductWizardShipping';
import ProductWizardStock from './ProductWizardStock';
import ProductWizardPublish from './ProductWizardPublish';
import './ProductWizard.css';

const DEFAULT_CUSTODIAN = 'MAYHEM MAKERS LLC, 8 THE GRN STE B 19901 DOVER DE';
const STOCK_TYPES = ['physical', 'service', 'custom', 'commission', 'digital'];
const SHIPPED_TYPES = ['physical', 'commission', 'subscription'];

async function readApiResponse(response) {
    const text = await response.text();
    if (!text) return {};

    try {
        return JSON.parse(text);
    } catch (e) {
        return {error: text, message: text};
    }
}

function getErrorMessage(body, fallback) {
    return body?.error || body?.message || fallback;
}

export default function ProductSubpane({productUuid, section, uiVersion = 2, onNavigate}) {
    const {fetch, API_URL, channel} = useAuth();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(section !== 'compliance');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const saveQueue = useRef(Promise.resolve());

    useEffect(() => {
        if (!productUuid || section === 'compliance') return;

        let active = true;
        setLoading(true);
        setError('');
        fetch(`${API_URL}/v1/seller/products/${productUuid}`)
            .then(async response => {
                const body = await readApiResponse(response);
                if (!response.ok) {
                    throw new Error(getErrorMessage(body, 'Could not load this product.'));
                }
                if (active) setProduct(body.data);
            })
            .catch(loadError => {
                if (active) setError(loadError.message);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [API_URL, productUuid, section]);

    const navigate = destination => {
        let path = destination === 'products'
            ? '/account/products'
            : `/account/products/${productUuid}/${destination}`;
        if (destination !== 'products') {
            path += `?uiVersion=${uiVersion}`;
        }
        if (onNavigate) {
            onNavigate(path);
            return;
        }
        window.location.assign(path);
    };

    const requestProductPatch = async (payload, {keepalive = false} = {}) => {
        const response = await fetch(`${API_URL}/v1/seller/products/${productUuid}`, {
            method: 'PATCH',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(payload),
            keepalive,
        });
        const body = await readApiResponse(response);
        if (!response.ok) {
            throw new Error(getErrorMessage(body, 'Could not save this product.'));
        }
        setProduct(body.data);
        return body.data;
    };

    const patchProduct = (payload, options) => {
        const save = saveQueue.current
            .catch(() => undefined)
            .then(() => requestProductPatch(payload, options));
        saveQueue.current = save;
        return save;
    };

    const handleAutosave = async payload => {
        setError('');
        setSaving(true);
        try {
            await patchProduct(payload, {keepalive: true});
        } catch (saveError) {
            setError(saveError.message);
            throw saveError;
        } finally {
            setSaving(false);
        }
    };

    const saveAndNavigate = async (payload, destination) => {
        setError('');
        if (payload !== null) {
            setSaving(true);
            try {
                await patchProduct(payload);
            } catch (saveError) {
                setError(saveError.message);
                setSaving(false);
                return;
            }
            setSaving(false);
        }
        navigate(destination);
    };

    const handleTranslationsChange = translations => {
        setProduct(current => current ? {...current, translations} : current);
    };

    // Publish pane toggles the listing on/off in place, keeping the user on the
    // page so the summary reflects the new status instead of navigating away.
    const updateListing = async payload => {
        setError('');
        setSaving(true);
        try {
            await patchProduct(payload);
        } catch (saveError) {
            setError(saveError.message);
        } finally {
            setSaving(false);
        }
    };

    const setAvailability = isAvailable => updateListing({isAvailable});

    if (section === 'compliance') {
        return (
            <ProductCompliancePage
                productUuid={productUuid}
                defaultCustodian={DEFAULT_CUSTODIAN}
            />
        );
    }

    if (loading) return <p>Loading product…</p>;
    if (!product) return <Alert type="error">{error || 'Could not load this product.'}</Alert>;

    const terminalSection = channel?.isRed ? 'compliance' : 'publish';
    const nextMediaSection = STOCK_TYPES.includes(product.type)
        ? 'stock'
        : SHIPPED_TYPES.includes(product.type) ? 'shipping' : terminalSection;
    const nextStockSection = SHIPPED_TYPES.includes(product.type)
        ? 'shipping'
        : terminalSection;
    const previousPublishSection = channel?.isRed
        ? 'compliance'
        : SHIPPED_TYPES.includes(product.type)
            ? 'shipping'
            : STOCK_TYPES.includes(product.type) ? 'stock' : 'media';

    let content;
    switch (section) {
        case 'basics':
            content = (
                <ProductWizardBasics
                    product={product}
                    onNext={payload => saveAndNavigate(payload, 'details')}
                    showListingControls
                    saving={saving}
                    error={error}
                    defaultLanguage={channel?.defaultLanguage}
                    defaultCurrency={channel?.defaultCurrency}
                    onTranslationsChange={handleTranslationsChange}
                    onAutosave={handleAutosave}
                />
            );
            break;
        case 'details':
            content = (
                <ProductWizardDetails
                    product={product}
                    onNext={payload => saveAndNavigate(payload, 'media')}
                    onBack={() => navigate('basics')}
                    onSaveAndExit={payload => saveAndNavigate(payload, 'products')}
                    saving={saving}
                    error={error}
                    defaultLanguage={channel?.defaultLanguage}
                    onTranslationsChange={handleTranslationsChange}
                    onAutosave={handleAutosave}
                />
            );
            break;
        case 'media':
            content = (
                <ProductWizardMedia
                    product={product}
                    onNext={payload => saveAndNavigate(payload, nextMediaSection)}
                    onBack={() => navigate('details')}
                    onSaveAndExit={payload => saveAndNavigate(payload, 'products')}
                    saving={saving}
                    error={error}
                    onAutosave={handleAutosave}
                />
            );
            break;
        case 'stock':
            content = (
                <ProductWizardStock
                    product={product}
                    onNext={payload => saveAndNavigate(payload, nextStockSection)}
                    onBack={() => navigate('media')}
                    onSaveAndExit={payload => saveAndNavigate(payload, 'products')}
                    onAutosave={handleAutosave}
                    error={error}
                />
            );
            break;
        case 'shipping':
            content = (
                <ProductWizardShipping
                    product={product}
                    channel={channel}
                    onNext={payload => saveAndNavigate(payload, terminalSection)}
                    onBack={() => navigate(
                        STOCK_TYPES.includes(product.type) ? 'stock' : 'media',
                    )}
                    onSaveAndExit={payload => saveAndNavigate(payload, 'products')}
                    saving={saving}
                    error={error}
                    onAutosave={handleAutosave}
                />
            );
            break;
        case 'publish':
            content = (
                <ProductWizardPublish
                    product={product}
                    channel={channel}
                    onPublish={() => setAvailability(true)}
                    onSaveDraft={() => setAvailability(false)}
                    onSetMarketplace={inMarketplace => updateListing({inMarketplace})}
                    onBack={() => navigate(previousPublishSection)}
                    onDismiss={() => navigate('products')}
                    showListingControls
                    saving={saving}
                    error={error}
                />
            );
            break;
        default:
            content = <Alert type="error">Unknown product section.</Alert>;
    }

    return (
        <section className={`product-wizard product-subpane product-subpane--${section}`}>
            {content}
        </section>
    );
}
